// Modo demonstração pela interface (tarefa 5.6): liga e desliga os agentes fictícios sem reiniciar
// o servidor (GET e POST /api/demo, src/http/server.ts). Três peças:
// - faixa no topo do palco, com o selo "demonstração" (o admin clica para desligar);
// - botão "Ver demonstração" no centro, com o escritório vazio (só admin);
// - ?mock=1 na URL pede para ligar (só admin; quem é membro não liga nada).
// Cada agente fictício leva o selo no painel (seloPainel). As regras são funções puras
// (tests/demo-web.test.ts); o resto só mexe no DOM.

import { t } from './i18n.js';

/** Prefixo dos ids dos agentes fictícios (src/adapters/demo/scenario.ts). */
const PREFIXO = 'demo:';

/** O agente é fictício? O mesmo critério do Meu dia e do registro. */
export function ehDemo(agente: { id: string }): boolean {
  return agente.id.startsWith(PREFIXO);
}

export interface EstadoDemo {
  ligado: boolean;
  admin: boolean;
  /** Agentes no escritório agora (reais e fictícios). */
  agentes: number;
}

/** O convite aparece para o admin, com o escritório vazio e a demonstração desligada. */
export function mostraConvite(estado: EstadoDemo): boolean {
  return !estado.ligado && estado.admin && estado.agentes === 0;
}

/** ?mock=1 pede para ligar: só vale se a demonstração estiver desligada e quem pede for admin. */
export function pedeLigarPelaUrl(busca: string, estado: Pick<EstadoDemo, 'ligado' | 'admin'>): boolean {
  return !estado.ligado && estado.admin && new URLSearchParams(busca).get('mock') === '1';
}

/** Selo "demonstração" no painel de um agente fictício (vazio para os reais). */
export function seloPainel(agente: { id: string }): string {
  return ehDemo(agente) ? `<span class="selo-demo" title="${t('demo.painel')}">${t('demo.selo')}</span>` : '';
}

interface RespostaDemo {
  ok?: boolean;
  ligado?: boolean;
  error?: string;
}

let estado: EstadoDemo = { ligado: false, admin: false, agentes: 0 };
let faixa: HTMLElement | undefined;
let centro: HTMLElement | undefined;
let convite: HTMLButtonElement | undefined;
let dica: HTMLElement | undefined;
let aviso: HTMLElement | undefined;

/** Monta a faixa e o convite no palco e lê o estado do servidor. Chamado depois do login. */
export async function montaDemo(admin: boolean, busca = location.search): Promise<void> {
  const palco = document.querySelector<HTMLElement>('#stage');
  if (!palco || faixa) return;
  estado = { ...estado, admin };

  faixa = document.createElement('div');
  faixa.id = 'demo-faixa';
  faixa.className = 'demo-faixa';
  faixa.hidden = true;
  faixa.addEventListener('click', (evento) => {
    if ((evento.target as HTMLElement).closest('[data-demo-desligar]')) void muda(false);
  });

  centro = document.createElement('div');
  centro.id = 'demo-centro';
  centro.className = 'demo-centro';
  centro.hidden = true;
  convite = document.createElement('button');
  convite.type = 'button';
  convite.id = 'demo-convite';
  convite.className = 'convite-demo';
  convite.addEventListener('click', () => void muda(true));
  dica = document.createElement('p');
  dica.className = 'demo-dica';
  centro.append(convite, dica);
  aviso = document.createElement('p');
  aviso.className = 'demo-erro';
  aviso.setAttribute('role', 'alert');
  aviso.hidden = true;

  palco.append(faixa, centro, aviso);

  const r = await le(fetch('/api/demo'));
  estado = { ...estado, ligado: r?.ligado === true };
  if (pedeLigarPelaUrl(busca, estado)) await muda(true);
  pinta();
}

/** Estado que chegou do servidor (evento 'demo' do SSE ou resposta do POST). */
export function aplicaDemo(ligado: boolean): void {
  estado = { ...estado, ligado };
  pinta();
}

/** Quantos agentes há no escritório agora: decide se o convite aparece. Chamado a cada render. */
export function atualizaDemo(agentes: number, admin: boolean): void {
  estado = { ...estado, agentes, admin };
  pinta();
}

async function muda(ligado: boolean): Promise<void> {
  const r = await le(
    fetch('/api/demo', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ligado }),
    }),
  );
  if (r?.ok && typeof r.ligado === 'boolean') {
    if (aviso) aviso.hidden = true;
    aplicaDemo(r.ligado);
    return;
  }
  if (aviso) {
    aviso.textContent = t('demo.erro', { erro: r?.error ?? t('demo.sem_resposta') });
    aviso.hidden = false;
  }
}

/** Lê o JSON da resposta; sem rede ou sem JSON, devolve null (a tela segue como está). */
async function le(pedido: Promise<Response>): Promise<RespostaDemo | null> {
  try {
    return (await (await pedido).json()) as RespostaDemo;
  } catch {
    return null;
  }
}

function pinta(): void {
  if (!faixa || !centro || !convite || !dica) return;
  const ligado = estado.ligado;
  faixa.hidden = !ligado;
  if (ligado) {
    const selo = `<span class="selo-demo" title="${t('demo.selo_titulo')}">${t('demo.selo')}</span>`;
    const texto = `<span class="demo-texto">${t('demo.faixa')}</span>`;
    const botao = estado.admin ? `<button type="button" class="demo-desligar" data-demo-desligar>${t('demo.desligar')}</button>` : '';
    const html = `${selo}${texto}${botao}`;
    if (faixa.innerHTML !== html) faixa.innerHTML = html;
  }
  const mostra = mostraConvite(estado);
  centro.hidden = !mostra;
  if (mostra && convite.textContent !== t('demo.ver')) {
    convite.textContent = t('demo.ver');
    dica.textContent = t('demo.selo_titulo');
  }
}
